import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["capability"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * Determines which capability is more desirable.
 * Users desire direct access over request access since it's instant.
 * @returns 0 if equal, -1 if a is more desirable, 1 if b is more desirable.
 */
var compareCapability = function compareCapability(a, b) {
  if (a === b) {
    return 0;
  }
  return a === 'DIRECT_ACCESS' ? -1 : 1;
};

/**
 * Determines which mode is more desirable.
 * Atlassian prefers to provide open access over domain restricted access.
 * Both of these modes have already been enabled by an admin.
 * If mode is undefined, it is deemed least desirable.
 * @returns 0 if equal, -1 if a is more open, 1 if b is more open.
 */
var compareMode = function compareMode(a, b) {
  if (a === b) {
    return 0;
  }
  if (!a) {
    return 1;
  }
  if (!b) {
    return -1;
  }
  return a === 'OPEN' ? -1 : 1;
};

/**
 * Determine if a recommendation is more desirable than another.
 * Atlassian prefers a better capability over any mode setting.
 * The mode will only be compared if the capabilities are equal.
 * If both capability and mode are equal, the recommendations are considered the same.
 *
 * Assumes recommendations have the same resource ID.
 * Do not compare recommendations with different resource IDs, as it does not make sense.
 *
 * @returns 0 if capability and mode are equal, -1 if a is more desirable, 1 if b is more desirable.
 */
var compareRecommendations = function compareRecommendations(a, b) {
  if (a.resourceId !== b.resourceId) {
    throw new Error('Recommendations must have the same resource ID');
  }
  if (compareCapability(a.capability, b.capability) < 0) {
    return -1;
  } else if (compareCapability(a.capability, b.capability) > 0) {
    return 1;
  } else if (compareMode(a.mode, b.mode) < 0) {
    return -1;
  } else if (compareMode(a.mode, b.mode) > 0) {
    return 1;
  }
  return 0;
};
var toRecommendation = function toRecommendation(recommendation) {
  var capability = recommendation.capability,
    rest = _objectWithoutProperties(recommendation, _excluded);
  return _objectSpread({}, rest);
};

/**
 * Removes duplicate recommendations with the same resource ARI.
 * When a collision occurs, select the recommendation with the most desirable capability and mode.
 * @returns a recommendation response with unique resource ARIs.
 */
export var removeDuplicateRecommendations = function removeDuplicateRecommendations(recommendations) {
  var _recommendations$capa = recommendations.capability,
    DIRECT_ACCESS = _recommendations$capa.DIRECT_ACCESS,
    REQUEST_ACCESS = _recommendations$capa.REQUEST_ACCESS;
  var uniqueRecommendations = {};
  var allRecommendations = [].concat(_toConsumableArray(DIRECT_ACCESS.map(function (recommendation) {
    return _objectSpread(_objectSpread({}, recommendation), {}, {
      capability: 'DIRECT_ACCESS'
    });
  })), _toConsumableArray(REQUEST_ACCESS.map(function (recommendation) {
    return _objectSpread(_objectSpread({}, recommendation), {}, {
      capability: 'REQUEST_ACCESS'
    });
  })));
  allRecommendations.forEach(function (recommendation) {
    var existing = uniqueRecommendations[recommendation.resourceId];
    if (existing && compareRecommendations(existing, recommendation) <= 0) {
      return;
    }
    uniqueRecommendations[recommendation.resourceId] = recommendation;
  });
  var allUniqueRecommendations = Object.values(uniqueRecommendations);
  return {
    capability: {
      DIRECT_ACCESS: allUniqueRecommendations.filter(function (recommendation) {
        return recommendation.capability === 'DIRECT_ACCESS';
      }).map(toRecommendation),
      REQUEST_ACCESS: allUniqueRecommendations.filter(function (recommendation) {
        return recommendation.capability === 'REQUEST_ACCESS';
      }).map(toRecommendation)
    }
  };
};